import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaSearch,
  FaSpinner,
  FaExchangeAlt,
  FaGavel,
  FaTag,
  FaShoppingCart,
  FaClock,
  FaThLarge,
  FaList,
  FaSync,
  FaFilter
} from 'react-icons/fa';
import '../styles/StatsPage.css';

const BASE_URL = 'https://api.mainnet.klever.org/v1.0';
const api = axios.create({ baseURL: BASE_URL, timeout: 15000 });

// Cache for collection info (logo, name) - persists across renders
const collectionCache = {};

// Cache for asset type checks
const assetTypeCache = {};

// Cache for individual NFT images
const nftImageCache = {};

// IPFS gateways to try
const IPFS_GATEWAYS = [
  'https://ipfs.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://dweb.link/ipfs/',
];

// Convert IPFS URI to HTTP URL
const ipfsToHttp = (uri, gatewayIndex = 0) => {
  if (!uri) return '';
  if (uri.startsWith('http')) return uri;
  if (uri.startsWith('ipfs://')) {
    const hash = uri.replace('ipfs://', '');
    return IPFS_GATEWAYS[gatewayIndex % IPFS_GATEWAYS.length] + hash;
  }
  // Assume raw IPFS hash
  if (uri.startsWith('Qm') || uri.startsWith('bafy')) {
    return IPFS_GATEWAYS[gatewayIndex % IPFS_GATEWAYS.length] + uri;
  }
  return uri;
};

const StatsPage = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collectionInfo, setCollectionInfo] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');
  const [allMarketplaces, setAllMarketplaces] = useState({});
  const [nftImages, setNftImages] = useState({});
  const collectionLoadQueue = useRef([]);
  const nftImageQueue = useRef([]);
  const isLoadingCollections = useRef(false);
  const isLoadingNftImages = useRef(false);

  // Fetch all marketplaces from API on mount (handle pagination)
  useEffect(() => {
    const fetchMarketplaces = async () => {
      try {
        const mpMap = {};
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await api.get(`/marketplaces/list?page=${page}`);
          const data = response.data?.data;
          const marketplaces = data?.marketplaces || [];
          const pagination = response.data?.pagination;

          marketplaces.forEach(mp => {
            if (mp.id && mp.name) {
              mpMap[mp.id] = mp.name;
            }
          });

          // Check if there are more pages
          if (pagination && pagination.next > page) {
            page = pagination.next;
          } else {
            hasMore = false;
          }
        }

        setAllMarketplaces(mpMap);
      } catch (err) {
        console.error('Error fetching marketplaces:', err);
        // Fallback to known marketplaces
        setAllMarketplaces({
          '417b70c0eb7a33cb': 'Rare Canvas',
          'd4f2bab340c55fde': 'Klever NFT',
          '7c353c02770da029': 'Sky Gallery',
          '2936933ee43db509': 'Minthree',
        });
      }
    };
    fetchMarketplaces();
  }, []);

  // Check if an asset is an NFT (not fungible token)
  const isNFTAsset = async (collectionId) => {
    if (assetTypeCache[collectionId] !== undefined) {
      return assetTypeCache[collectionId];
    }
    try {
      const res = await api.get(`/assets/${collectionId}`);
      const asset = res.data?.data?.asset;
      const isNFT = asset?.assetType === 'NonFungible' ||
                   asset?.assetType === 'SemiFungible' ||
                   (asset?.properties?.canMint && asset?.maxSupply > 0);
      assetTypeCache[collectionId] = isNFT;

      // Also cache collection info while we have it
      if (!collectionCache[collectionId]) {
        collectionCache[collectionId] = {
          name: asset?.name || collectionId,
          logo: asset?.logo || '',
        };
      }
      return isNFT;
    } catch {
      assetTypeCache[collectionId] = true;
      return true;
    }
  };

  // Fetch collection info (logo, name) - uses cache
  const fetchCollectionInfo = async (collectionId) => {
    if (collectionCache[collectionId]) {
      return collectionCache[collectionId];
    }
    try {
      const res = await api.get(`/assets/${collectionId}`);
      const asset = res.data?.data?.asset;
      const info = {
        name: asset?.name || collectionId,
        logo: asset?.logo || '',
      };
      collectionCache[collectionId] = info;
      return info;
    } catch {
      const info = { name: collectionId, logo: '' };
      collectionCache[collectionId] = info;
      return info;
    }
  };

  // Fetch individual NFT image from asset
  const fetchNFTImage = async (assetId) => {
    if (nftImageCache[assetId]) {
      return nftImageCache[assetId];
    }
    try {
      const res = await api.get(`/assets/${assetId}`);
      const asset = res.data?.data?.asset;

      // Try to get image from various sources
      let image = '';

      // 1. Check logo field
      if (asset?.logo) {
        image = asset.logo;
      }
      // 2. Check uris array
      else if (asset?.uris && asset.uris.length > 0) {
        // Find image URI (often has key 'image' or is first URI)
        const imageUri = asset.uris.find(u =>
          u.key?.toLowerCase() === 'image' ||
          u.value?.includes('.png') ||
          u.value?.includes('.jpg') ||
          u.value?.includes('.gif') ||
          u.value?.includes('ipfs')
        );
        if (imageUri) {
          image = ipfsToHttp(imageUri.value);
        } else if (asset.uris[0]?.value) {
          // Try first URI as metadata and fetch it
          const metadataUrl = ipfsToHttp(asset.uris[0].value);
          try {
            const metaRes = await axios.get(metadataUrl, { timeout: 5000 });
            if (metaRes.data?.image) {
              image = ipfsToHttp(metaRes.data.image);
            }
          } catch {
            // Metadata fetch failed, use first URI directly
            image = metadataUrl;
          }
        }
      }

      nftImageCache[assetId] = image;
      return image;
    } catch {
      nftImageCache[assetId] = '';
      return '';
    }
  };

  // Process collection queue - batch update state
  const processCollectionQueue = useCallback(async () => {
    if (isLoadingCollections.current || collectionLoadQueue.current.length === 0) return;
    isLoadingCollections.current = true;

    const batch = {};
    while (collectionLoadQueue.current.length > 0) {
      const collectionId = collectionLoadQueue.current.shift();
      if (!collectionCache[collectionId] && !batch[collectionId]) {
        const info = await fetchCollectionInfo(collectionId);
        batch[collectionId] = info;
      }
    }

    // Single state update with all fetched collections
    if (Object.keys(batch).length > 0) {
      setCollectionInfo(prev => ({ ...prev, ...batch }));
    }

    isLoadingCollections.current = false;
  }, []);

  // Queue collection for loading
  const queueCollection = useCallback((collectionId) => {
    if (collectionCache[collectionId] || collectionInfo[collectionId]) return;
    if (!collectionLoadQueue.current.includes(collectionId)) {
      collectionLoadQueue.current.push(collectionId);
      // Debounce processing
      setTimeout(() => processCollectionQueue(), 50);
    }
  }, [collectionInfo, processCollectionQueue]);

  // Process NFT image queue - batch update state
  const processNftImageQueue = useCallback(async () => {
    if (isLoadingNftImages.current || nftImageQueue.current.length === 0) return;
    isLoadingNftImages.current = true;

    const batch = {};
    // Process up to 5 at a time to avoid too many requests
    const toProcess = nftImageQueue.current.splice(0, 5);

    await Promise.all(toProcess.map(async (assetId) => {
      if (!nftImageCache[assetId] && !batch[assetId]) {
        const image = await fetchNFTImage(assetId);
        batch[assetId] = image;
      }
    }));

    // Single state update with all fetched images
    if (Object.keys(batch).length > 0) {
      setNftImages(prev => ({ ...prev, ...batch }));
    }

    isLoadingNftImages.current = false;

    // Process more if queue still has items
    if (nftImageQueue.current.length > 0) {
      setTimeout(() => processNftImageQueue(), 100);
    }
  }, []);

  // Queue NFT image for loading
  const queueNftImage = useCallback((assetId) => {
    if (nftImageCache[assetId] || nftImages[assetId] !== undefined) return;
    if (!nftImageQueue.current.includes(assetId)) {
      nftImageQueue.current.push(assetId);
      // Debounce processing
      setTimeout(() => processNftImageQueue(), 100);
    }
  }, [nftImages, processNftImageQueue]);

  // Get collection display info (from cache or state)
  const getCollectionDisplay = (assetId) => {
    const collectionId = assetId?.split('/')[0] || assetId;
    return collectionCache[collectionId] || collectionInfo[collectionId] || { name: collectionId, logo: '' };
  };

  // Get NFT image (individual NFT image > collection logo > placeholder)
  const getNftImage = (assetId) => {
    // First check if we have individual NFT image
    const nftImg = nftImageCache[assetId] || nftImages[assetId];
    if (nftImg) return nftImg;

    // Fall back to collection logo
    const collectionId = assetId?.split('/')[0] || assetId;
    const collInfo = collectionCache[collectionId] || collectionInfo[collectionId];
    return collInfo?.logo || '';
  };

  // Fetch recent listings (type 18 = SellContract)
  const fetchListings = useCallback(async () => {
    try {
      const response = await api.get('/transaction/list?limit=60&type=18&status=success');
      const transactions = response.data.data.transactions || [];

      const rawItems = transactions.flatMap(tx => {
        return (tx.contract || [])
          .filter(c => c.typeString === 'SellContractType')
          .map(c => {
            const p = c.parameter || {};
            const sellReceipt = tx.receipts?.find(r => r.typeString === 'Sell');
            return {
              hash: tx.hash,
              timestamp: tx.timestamp,
              seller: tx.sender,
              assetId: p.assetId || '',
              price: p.price || 0,
              currencyId: p.currencyID || 'KLV',
              marketType: p.marketType || 'BuyItNowMarket',
              endTime: p.endTime || 0,
              marketplaceId: p.marketplaceID || '',
              orderId: sellReceipt?.orderId || '',
            };
          });
      }).filter(item => item.assetId && item.price > 0 && item.assetId.includes('/'));

      // Filter to only NFTs
      const nftItems = [];
      for (const item of rawItems) {
        const collId = item.assetId.split('/')[0];
        const isNFT = await isNFTAsset(collId);
        if (isNFT) nftItems.push(item);
        if (nftItems.length >= 40) break;
      }

      setListings(nftItems);

      // Queue collections for info loading
      const uniqueCollections = [...new Set(nftItems.map(item => item.assetId.split('/')[0]))];
      uniqueCollections.forEach(queueCollection);

      // Queue NFT images for loading
      nftItems.forEach(item => queueNftImage(item.assetId));
    } catch (err) {
      console.error('Error fetching listings:', err);
    }
  }, [queueCollection, queueNftImage]);

  // Fetch recent sales (type 17, MarketBuy)
  const fetchSales = useCallback(async () => {
    try {
      const response = await api.get('/transaction/list?limit=60&type=17&status=success');
      const transactions = response.data.data.transactions || [];

      const items = transactions
        .map(tx => {
          const buyContract = tx.contract?.find(c => c.typeString === 'BuyContractType');
          if (!buyContract) return null;
          const p = buyContract.parameter || {};
          if (p.buyType !== 'MarketBuy') return null;

          const buyReceipt = tx.receipts?.find(r =>
            r.typeString === 'Transfer' &&
            (r.assetType === 'NonFungible' || r.assetType === 'SemiFungible') &&
            r.assetId?.includes('/')
          );

          if (!buyReceipt) return null;

          return {
            hash: tx.hash,
            timestamp: tx.timestamp,
            buyer: tx.sender,
            seller: buyReceipt.from || '',
            assetId: buyReceipt.assetId || '',
            price: p.currencyAmount || p.amount || 0,
            currencyId: p.currencyID || p.currencyId || 'KLV',
          };
        })
        .filter(item => item && item.assetId && item.assetId.includes('/'))
        .slice(0, 40);

      setSales(items);

      // Queue collections for info loading
      const uniqueCollections = [...new Set(items.map(item => item.assetId.split('/')[0]))];
      uniqueCollections.forEach(queueCollection);

      // Queue NFT images for loading
      items.forEach(item => queueNftImage(item.assetId));
    } catch (err) {
      console.error('Error fetching sales:', err);
    }
  }, [queueCollection, queueNftImage]);

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    await Promise.all([fetchListings(), fetchSales()]);
    setLoading(false);
    setRefreshing(false);
  }, [fetchListings, fetchSales]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const formatKLV = (raw) => {
    if (!raw) return '0';
    const klv = raw / 1000000;
    if (klv >= 1000000) return (klv / 1000000).toFixed(1) + 'M';
    if (klv >= 1000) return (klv / 1000).toFixed(1) + 'K';
    if (klv >= 1) return klv.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return klv.toFixed(2);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() / 1000 - ts;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getCollectionId = (assetId) => assetId?.split('/')[0] || assetId;
  const getNonce = (assetId) => assetId?.split('/')[1] || '';

  const filteredListings = listings.filter(l => {
    if (marketplaceFilter !== 'all' && l.marketplaceId !== marketplaceFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const display = getCollectionDisplay(l.assetId);
    return l.assetId.toLowerCase().includes(q) ||
      display.name.toLowerCase().includes(q);
  });

  const filteredSales = sales.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const display = getCollectionDisplay(s.assetId);
    return s.assetId.toLowerCase().includes(q) ||
      display.name.toLowerCase().includes(q);
  });

  const activeItems = activeTab === 'listings' ? filteredListings : filteredSales;

  const isAuction = (item) => item.marketType === 'AuctionMarket';

  const getMarketplaceName = (id) => allMarketplaces[id] || 'Marketplace';

  return (
    <div className="explore-page">
      {/* Hero */}
      <div className="explore-hero">
        <div className="explore-hero-inner">
          <h1>Explore NFTs</h1>
          <p>Discover, collect, and trade NFTs across KleverChain marketplaces</p>
          <div className="explore-search">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by collection name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="explore-toolbar">
        <div className="explore-tabs">
          <button
            className={`explore-tab ${activeTab === 'listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('listings')}
          >
            <FaTag /> New Listings
            <span className="tab-count">{filteredListings.length}</span>
          </button>
          <button
            className={`explore-tab ${activeTab === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveTab('sales')}
          >
            <FaShoppingCart /> Recent Sales
            <span className="tab-count">{filteredSales.length}</span>
          </button>
        </div>
        <div className="explore-controls">
          {/* Marketplace filter (only for listings) */}
          {activeTab === 'listings' && Object.keys(allMarketplaces).length > 0 && (
            <div className="marketplace-filter">
              <FaFilter className="filter-icon" />
              <select
                value={marketplaceFilter}
                onChange={(e) => setMarketplaceFilter(e.target.value)}
              >
                <option value="all">All Marketplaces</option>
                {Object.entries(allMarketplaces).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Refresh button */}
          <button
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={() => loadData(true)}
            disabled={refreshing}
            title="Refresh"
          >
            <FaSync />
          </button>
          {/* View toggle */}
          <div className="view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FaThLarge />
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FaList />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="explore-loading">
          <FaSpinner className="spinner" />
          <span>Scanning KleverChain...</span>
        </div>
      ) : activeItems.length === 0 ? (
        <div className="explore-empty">
          <p>No {activeTab === 'listings' ? 'listings' : 'sales'} found{searchQuery ? ` for "${searchQuery}"` : ''}.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="explore-grid">
          {activeItems.map((item, i) => {
            const collId = getCollectionId(item.assetId);
            const nonce = getNonce(item.assetId);
            const display = getCollectionDisplay(item.assetId);

            return (
              <Link
                key={item.hash + i}
                to={`/collections/${collId}${nonce ? `/${nonce}` : ''}`}
                className="nft-card"
              >
                {/* Image */}
                <div className="nft-card-image">
                  {getNftImage(item.assetId) ? (
                    <img
                      src={getNftImage(item.assetId)}
                      alt=""
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="nft-card-placeholder">
                      <span>{collId.charAt(0)}</span>
                    </div>
                  )}

                  {/* Badges */}
                  <div className="nft-card-badges">
                    {activeTab === 'listings' && isAuction(item) && (
                      <span className="badge auction"><FaGavel /> Auction</span>
                    )}
                    {activeTab === 'listings' && !isAuction(item) && (
                      <span className="badge buy-now"><FaTag /> Buy Now</span>
                    )}
                    {activeTab === 'sales' && (
                      <span className="badge sold"><FaExchangeAlt /> Sold</span>
                    )}
                  </div>

                  {/* Time */}
                  <div className="nft-card-time">
                    <FaClock /> {timeAgo(item.timestamp)}
                  </div>
                </div>

                {/* Info */}
                <div className="nft-card-body">
                  <div className="nft-card-collection">
                    {display.name}
                  </div>
                  <div className="nft-card-name">
                    {nonce ? `#${nonce}` : item.assetId}
                  </div>
                  <div className="nft-card-footer">
                    <div className="nft-card-price">
                      <span className="price-label">
                        {activeTab === 'listings' ? 'Price' : 'Sold for'}
                      </span>
                      <span className="price-value">
                        {formatKLV(item.price)}
                        <small>{item.currencyId}</small>
                      </span>
                    </div>
                    {activeTab === 'listings' && item.marketplaceId && (
                      <div className="nft-card-marketplace">
                        {getMarketplaceName(item.marketplaceId)}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="explore-list">
          <div className="list-header">
            <span className="l-item">NFT</span>
            <span className="l-price">Price</span>
            <span className="l-from">{activeTab === 'listings' ? 'Seller' : 'Buyer'}</span>
            <span className="l-market">{activeTab === 'listings' ? 'Marketplace' : 'Type'}</span>
            <span className="l-time">Time</span>
          </div>
          {activeItems.map((item, i) => {
            const collId = getCollectionId(item.assetId);
            const nonce = getNonce(item.assetId);
            const display = getCollectionDisplay(item.assetId);
            const addr = activeTab === 'listings' ? item.seller : item.buyer;

            return (
              <Link
                key={item.hash + i}
                to={`/collections/${collId}${nonce ? `/${nonce}` : ''}`}
                className="list-row"
              >
                <span className="l-item">
                  <div className="list-thumb">
                    {getNftImage(item.assetId) ? (
                      <img src={getNftImage(item.assetId)} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="list-thumb-ph">{collId.charAt(0)}</div>
                    )}
                  </div>
                  <div className="list-nft-info">
                    <span className="list-nft-name">{display.name}</span>
                    <span className="list-nft-id">{nonce ? `#${nonce}` : item.assetId}</span>
                  </div>
                </span>
                <span className="l-price">
                  <span className="list-price-val">{formatKLV(item.price)}</span>
                  <span className="list-price-cur">{item.currencyId}</span>
                </span>
                <span className="l-from">
                  {addr ? `${addr.slice(0, 8)}...${addr.slice(-4)}` : '-'}
                </span>
                <span className="l-market">
                  {activeTab === 'listings' ? (
                    getMarketplaceName(item.marketplaceId) || '-'
                  ) : (
                    <span className="sold-badge"><FaExchangeAlt /> Sale</span>
                  )}
                </span>
                <span className="l-time">{timeAgo(item.timestamp)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatsPage;
